import classNames from 'classnames'
import { X } from 'lucide-react'
import { useState, useEffect, useRef, type ReactNode } from 'react'
import { ownedPortal } from '../../lib/navigation/ownedPortal'
import { MOTION } from '../../utils/motion'
import { Text } from './Text'
import { useFocusTrap } from '../../hooks/useFocusTrap'
import { getOverlayHost } from '../../lib/layout/overlayHost'
import { lockAppScroll } from '../../lib/layout/appScroll'
import { useSheetDismissDrag } from '../../lib/layout/useSheetDismissDrag'
import { useOverlayHistory } from '../../lib/navigation/overlayHistory'

type SheetPriority = 'base' | 'above' | 'top'

/**
 * Prioridade → token de z-index.
 *
 * Tabela, e não `priority === 'x' ? … : …`: a forma ternária tem um default
 * silencioso, então um degrau novo cairia em `--z-overlay` sem erro nenhum —
 * exatamente o modo de falha que o BUG-127 já custou (folha montada e
 * invisível, sem sintoma no console). `Record<SheetPriority, …>` faz o
 * compilador cobrar as duas entradas de qualquer degrau que nasça depois.
 */
const OVERLAY_Z: Record<SheetPriority, string> = {
  base:  '--z-overlay',
  above: '--z-overlay-above',
  top:   '--z-overlay-top',
}
const MODAL_Z: Record<SheetPriority, string> = {
  base:  '--z-modal',
  above: '--z-modal-above',
  top:   '--z-modal-top',
}

export interface SheetProps {
  open:     boolean
  onClose:  () => void
  title?:   string
  /**
   * ⚠️ **O conteúdo NÃO recebe padding horizontal.** Quem monta decide, porque
   * as duas famílias querem coisas opostas: lista de ações é full-bleed (o
   * `MenuItemButton` traz o próprio `px-4` e o realce de toque tem de sangrar
   * até a borda) e prosa precisa de gutter. Não há default que sirva aos dois.
   *
   * Prosa/formulário → embrulhe em `px-page-x` (20px) ou `px-4` quando a folha
   * também tiver itens de menu, para os dois alinharem.
   *
   * Esquecer isso já foi para produção duas vezes (`DivisionSheet` 11/08 e
   * `PrivateLeagueSettingsSheet` 10/08, as duas rodadas que saíram sem passar
   * por navegador): o texto encosta nas duas bordas e a folha lê como se não
   * tivesse CSS.
   */
  children: ReactNode
  /**
   * Altura máxima como fração da viewport, ex: '80dvh'. Default: '90dvh'.
   *
   * Prefira `dvh` a `vh`: no iOS `vh` é a viewport **maior** (barra de
   * endereço recolhida), então uma folha ancorada em `bottom-0` com `90vh`
   * nasce mais alta que a área visível e perde o topo — handle e título — sob
   * a barra do navegador.
   */
  maxHeight?: string
  /**
   * Prioridade de empilhamento. `base` (default) usa --z-overlay/--z-modal.
   * `above` usa --z-overlay-above/--z-modal-above pra renderizar acima de
   * outro sheet/modal já aberto (ex: DuplicateBookModal sobre AddToShelfSheet).
   *
   * `top` (--z-overlay-top/--z-modal-top, 10000/10001) sobe acima da **prisão
   * do Modo Foco**, que é `--z-focus: 9999`. Mesmo vocabulário e mesmos tokens
   * do `StudioShellV3`, e pela mesma razão medida no BUG-127: a folha sai por
   * portal, então **deixa de herdar a pilha de quem a abre** e passa a disputar
   * com ela — e 90 < 9999. O sintoma não é um z errado na tela, é a folha
   * montar inteira, com geometria correta, atrás de uma superfície OPACA: ela
   * simplesmente não aparece e não recebe toque.
   *
   * ⚠️ Só use `top` a partir de um host que já esteja acima de `--z-modal`.
   * Numa folha comum ele a poria na frente do toast (110) sem motivo.
   */
  priority?: SheetPriority
  /**
   * Exibe o botão "X" de fechar. Default `true`.
   *
   * ⚠️ O texto anterior dizia "o handle do topo é decorativo (não há
   * swipe-to-dismiss)" — deixou de ser verdade em 05/09 (BUG-214): arrastar a
   * folha para baixo fecha. O ✕ continua sendo o default assim mesmo, porque
   * gesto não se anuncia sozinho e nem todo mundo o tenta; ele é a saída
   * visível, o arrasto é o atalho.
   * Só desligue em fluxo que exige decisão explícita do usuário.
   */
  showClose?: boolean
}

export function Sheet({ open, onClose, title, children, maxHeight = '90dvh', priority = 'base', showClose = true }: SheetProps) {
  const [isClosing, setIsClosing] = useState(false)
  const sheetRef                  = useRef<HTMLDivElement>(null)
  useFocusTrap(open && !isClosing, sheetRef)

  /** Timer do fechamento por dentro — cancelado se a folha sumir antes. */
  const selfCloseTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  useEffect(() => () => clearTimeout(selfCloseTimer.current), [])

  /**
   * `open` do render anterior. É o que permite reagir à mudança de prop NA FASE
   * DE RENDER, e não num efeito.
   *
   * ⚠️ Isto é o coração do fix de 13/08 e a diferença não é estilística. A saída
   * pelo pai já existia, mas começava num `useEffect` — que é PASSIVO, ou seja,
   * roda depois da pintura. A ordem real era:
   *
   *   1. render com `open=false`, `isClosing=false` → `return null` (guard abaixo)
   *   2. o navegador PINTA um frame sem a folha
   *   3. o efeito roda, marca `isClosing` e a folha REMONTA com `animate-sheet-down`
   *
   * Resultado na tela: a folha some, reaparece inteira e só então desce — o
   * "entra e sai na mesma animação" que o fundador reportou na folha de cor de
   * banner. Medido por identidade de nó: `['mount','unmount','mount']`.
   *
   * Ajustar estado durante o render é o padrão documentado do React para "prop
   * mudou": ele re-executa o componente ANTES de commitar, então o guard nunca
   * chega a ver o intervalo em que a folha não deveria existir.
   */
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    // As três transições, numa expressão só:
    //   fecha pelo PAI (isClosing false) → começa a saída AGORA, no mesmo commit
    //   fecha por DENTRO (isClosing true) → já animou; agora é só desmontar
    //   reabre                            → cancela a saída em curso
    setIsClosing(!open && !isClosing)
  }

  /**
   * Arrastar para baixo fecha (BUG-214) — a segunda metade do BUG-189.
   *
   * `enabled` cai durante a saída: sem isso um segundo arrasto sobre a folha
   * que já está descendo disputaria o mesmo `transform` com a animação.
   *
   * O gesto chama o MESMO `handleClose` do ✕ — inclusive a animação de saída
   * dele, que é inofensiva aqui porque o `transform` do arrasto é `!important`
   * e vence a keyframe (ver o cabeçalho do hook).
   */
  useSheetDismissDrag({
    panelRef: sheetRef,
    enabled: open && !isClosing,
    onDismiss: () => handleClose(),
  })

  /**
   * Voltar do sistema (Android, gesto, navegador) fecha a FOLHA, não a página
   * de baixo — a folha empilha uma entrada de histórico enquanto está aberta
   * (BUG-71 ⑥, 24/09). O voltar sai pelo MESMO caminho do ✕: anima e avisa.
   */
  useOverlayHistory({
    // Saindo já não é "aberta": durante os 280ms da animação o `open` do pai
    // ainda é true, e o hook leria isso como "o dono recusou o voltar".
    open: open && !isClosing,
    onBack: () => {
      if (!isClosing) handleClose()
    },
  })

  function handleClose() {
    // O fechamento parte DAQUI (✕, backdrop, Escape): a folha anima e só então
    // avisa o pai. `isClosing` NÃO é baixado aqui — quem o baixa é a transição
    // de `open` acima. Baixar os dois no mesmo lote faria o bloco de render ler
    // "fechou pelo pai" e disparar uma SEGUNDA saída sobre o mesmo fechamento.
    setIsClosing(true)
    selfCloseTimer.current = setTimeout(onClose, MOTION.sheet)
  }

  /**
   * Desmonte ao fim da saída iniciada pelo PAI.
   *
   * A guarda `open` é o que separa os dois caminhos: no fechamento por dentro
   * `open` ainda é `true` e quem desmonta é o `onClose` acima (via transição de
   * prop). Não chama `onClose` — o pai já sabe; avisar de volta faria consumidor
   * com efeito colateral rodá-lo duas vezes.
   */
  useEffect(() => {
    if (open || !isClosing) return
    const t = setTimeout(() => setIsClosing(false), MOTION.sheet)
    return () => clearTimeout(t)
  }, [open, isClosing])

  /**
   * Trava a rolagem de fundo enquanto a folha está na tela.
   *
   * ⚠️ Isto não é higiene: sem o lock, o gesto de arrastar a folha ROLA A
   * PÁGINA ATRÁS enquanto a folha fica parada — e é assim que o defeito se
   * apresenta ao usuário ("o modal entra travado"). Medido em iPhone 13
   * emulado, com a folha aberta e arrasto começando no PUXADOR: a página de
   * trás andou 421px e o `scrollTop` da folha ficou em 0.
   *
   * Duas causas somam, e por isso o conserto tem duas camadas (a outra é o
   * `touch-action` no painel, abaixo):
   *
   *  · o painel em si não é um scroll container, então todo gesto que começa
   *    fora do miolo (handle, título, ✕) propaga direto para o documento;
   *  · quando o conteúdo CABE no scroller — o caso da `DivisionSheet` —
   *    `overscroll-behavior: contain` não tem o que conter (o range de rolagem
   *    é zero) e o Chrome encadeia para o documento. Medido: roda do mouse
   *    sobre o miolo levou o documento de 0 a 400px.
   *
   * `lockAppScroll` fala com o scroller ATIVO (o documento no mobile, o div do
   * frame no desktop) e devolve o valor anterior em vez de `""`, então folha
   * aberta sobre folha (`priority: 'above'`) não destrava a de baixo ao fechar.
   *
   * Vale enquanto a folha estiver MONTADA, saída inclusa: destravar no início
   * da animação de saída devolveria 280ms de rolagem sob uma folha ainda
   * visível.
   */
  useEffect(() => {
    if (!open && !isClosing) return
    return lockAppScroll()
  }, [open, isClosing])

  // Fecha com Escape
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Marca como tratado: a casca de desktop fecha por Esc as folhas que não
      // têm o próprio handler, e só as que ninguém tratou (`desktop/SharedDesktop`).
      e.preventDefault()
      handleClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (typeof document === 'undefined') return null

  if (!open && !isClosing) return null

  return ownedPortal(
    (
    <div
      className="fixed inset-0"
      style={{ zIndex: `var(${OVERLAY_Z[priority]})` }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Backdrop */}
      <div
        className={classNames(
          'absolute inset-0 bg-black/60',
          isClosing ? 'animate-backdrop-out' : 'animate-backdrop-in',
        )}
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Sheet */}
      <div
        ref={sheetRef}
        className={classNames(
          'absolute bottom-0 left-0 right-0 flex flex-col',
          'bg-surface rounded-t-3xl overflow-hidden border-t border-surface-alt',
          isClosing ? 'animate-sheet-down' : 'animate-sheet-up',
        )}
        style={{
          zIndex: `var(${MODAL_Z[priority]})`,
          maxHeight,
          backdropFilter: 'blur(var(--mode-blur))',
          /**
           * O painel não é uma superfície de pan — a segunda camada do fix.
           *
           * O lock acima resolve a roda do mouse, mas `overflow: hidden` no
           * body é IGNORADO pelo WebKit no iOS para gesto de toque, e o iPhone
           * é justamente onde o defeito foi reportado. `touch-action: none`
           * vale no WebKit e diz que o NAVEGADOR não mexe neste elemento.
           *
           * ⚠️ Desde 05/09 (BUG-214) quem mexe somos nós: o
           * `useSheetDismissDrag` escuta o toque aqui e arrasta a folha para
           * baixo até fechar. `none` é o que torna isso possível sem disputa —
           * o navegador não inicia pan nenhum, então todo o gesto chega ao
           * hook. O miolo devolve `pan-y` e o hook só assume o arrasto quando
           * o scroller de lá já está no topo.
           *
           * O miolo devolve `pan-y` (abaixo), então o scroll de dentro segue
           * intacto: `touch-action` se aplica ao elemento onde o toque COMEÇA,
           * não ao ancestral.
           */
          touchAction: 'none',
        }}
      >
        {/* Handle */}
        <div data-sheet-handle className="flex justify-center pt-3 pb-1 shrink-0">
          <div className="w-10 h-1 rounded-full bg-surface-alt" />
        </div>

        {title ? (
          <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-surface-alt shrink-0">
            <Text variant="heading-sm">{title}</Text>
            {showClose && <SheetCloseButton onClick={handleClose} floating={false} />}
          </div>
        ) : showClose && (
          // Sem título não há header pra ancorar o X — ele flutua sobre o
          // conteúdo. Fundo próprio porque o conteúdo pode ser imagem ou
          // câmera (CoverScanSheet), onde um ghost puro sumiria.
          <SheetCloseButton onClick={handleClose} floating />
        )}

        {/* Conteúdo com scroll */}
        <div
          className="overflow-y-auto overscroll-contain"
          style={{
            paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 16px)',
            // Devolve o pan vertical que o painel nega: aqui é a única área
            // que ROLA de verdade.
            touchAction: 'pan-y',
          }}
        >
          {children}
        </div>
      </div>
    </div>
    ),
    getOverlayHost(),
  )
}

/**
 * O X das folhas. O DESENHO é o do `IconButton` `sm` de sempre (36px, ícone de
 * 14px — as mesmas classes, no `<span>`), mas o ALVO de toque é o `<button>` de
 * 44px em volta (medido 21/10 na A1b: o X tinha 36px). A margem negativa
 * (no cabeçalho) e o `top`/`right` 4px menores (flutuando) mantêm o centro do
 * ícone e a altura do cabeçalho exatamente onde estavam.
 *
 * Mutação que o teste derruba: voltar o botão para 36px (`h-9 w-9`) ou tirar a
 * margem negativa (o cabeçalho cresce 8px e o miolo desce).
 */
function SheetCloseButton({ onClick, floating }: { onClick: () => void; floating: boolean }) {
  return (
    <button
      type="button"
      aria-label="Fechar"
      onClick={onClick}
      className={classNames(
        'group inline-flex h-11 w-11 shrink-0 items-center justify-center focus-visible:outline-none',
        floating ? 'absolute top-1 right-2 z-10' : '-my-1 -mr-3',
      )}
    >
      <span
        aria-hidden="true"
        className={classNames(
          'inline-flex items-center justify-center',
          'transition-all duration-200',
          'group-focus-visible:ring-2 group-focus-visible:ring-primary',
          'group-active:scale-95',
          floating
            ? 'bg-surface-alt text-text-main group-hover:text-secondary group-hover:bg-surface'
            : 'bg-transparent text-text-muted group-hover:text-text-main group-hover:bg-surface-alt/50',
          'min-w-[36px] min-h-[36px] rounded-lg',
          floating && 'bg-surface/90 backdrop-blur-sm shadow-sm',
        )}
      >
        <X size={14} aria-hidden="true" />
      </span>
    </button>
  )
}
